// hooks/useMessageProcessor.tsx
import { useEffect, useMemo, useRef } from "react";
import { useArtifactStore } from "@/stores/artifact-store";
import {
    parseArtifactElements,
    detectArtifactStart,
    extractStreamingArtifactContent,
    hasStreamingArtifacts,
} from "@/lib/artifact-parser";
import { ParsedArtifactData, ArtifactType } from "@/types/Artifact";

interface UseMessageProcessorProps {
    messageId: string;
    threadId: string;
    content: string;
    isStreaming: boolean;
    role: "user" | "assistant";
}

interface UseMessageProcessorReturn {
    displayContent: string;
    artifacts: ParsedArtifactData[];
    hasArtifacts: boolean;
}

export const useMessageProcessor = ({
    messageId,
    threadId,
    content,
    isStreaming,
    role,
}: UseMessageProcessorProps): UseMessageProcessorReturn => {
    const {
        addArtifact,
        startLoading,
        updateLoadingContent,
        finishLoading,
        getThreadLoadingArtifacts,
        getAllThreadItems,
    } = useArtifactStore();

    // Track artifacts being processed to prevent duplicates
    const processingArtifacts = useRef<Map<string, string>>(new Map());
    const completedArtifacts = useRef<Set<string>>(new Set());

    // Generate unique key for artifact
    const getArtifactKey = (type: string, title: string) => `${type}::${title}`;

    useEffect(() => {
        if (role !== "assistant") return;

        if (isStreaming && hasStreamingArtifacts(content)) {
            // Get current loading artifacts for this thread
            const currentLoadingArtifacts = getThreadLoadingArtifacts(threadId);

            // Detect artifact starts
            const artifactStarts = detectArtifactStart(content);

            artifactStarts.forEach((artifactInfo) => {
                const key = getArtifactKey(artifactInfo.type, artifactInfo.title);

                // Check if we're already processing this artifact
                if (processingArtifacts.current.has(key)) {
                    // Update existing loading artifact
                    const loadingId = processingArtifacts.current.get(key)!;

                    // Extract current content for this specific artifact
                    const extracted = extractStreamingArtifactContent(content, artifactInfo.type, artifactInfo.title);

                    if (extracted) {
                        updateLoadingContent(loadingId, {
                            content: extracted.content,
                            language: extracted.language || artifactInfo.language,
                        });
                    }
                } else if (!completedArtifacts.current.has(key)) {
                    // Check if this artifact already exists in loading state
                    const existingLoading = currentLoadingArtifacts.find(
                        (la) => la.type === artifactInfo.type && la.title === artifactInfo.title
                    );

                    if (!existingLoading) {
                        // Start new loading artifact
                        const loadingId = startLoading({
                            type: artifactInfo.type,
                            title: artifactInfo.title,
                            threadId,
                            language: artifactInfo.language,
                        });

                        processingArtifacts.current.set(key, loadingId);

                        // Immediately check for partial content
                        const extracted = extractStreamingArtifactContent(
                            content,
                            artifactInfo.type,
                            artifactInfo.title
                        );

                        if (extracted) {
                            updateLoadingContent(loadingId, {
                                content: extracted.content,
                                language: extracted.language || artifactInfo.language,
                            });
                        }
                    } else {
                        // Update reference to existing loading artifact
                        processingArtifacts.current.set(key, existingLoading.id);

                        // Update its content
                        const extracted = extractStreamingArtifactContent(
                            content,
                            artifactInfo.type,
                            artifactInfo.title
                        );

                        if (extracted) {
                            updateLoadingContent(existingLoading.id, {
                                content: extracted.content,
                                language: extracted.language || artifactInfo.language,
                            });
                        }
                    }
                }
            });
        } else if (!isStreaming) {
            // Streaming completed - parse and finalize artifacts
            const { artifacts } = parseArtifactElements(content);

            artifacts.forEach((artifact) => {
                const key = getArtifactKey(artifact.type, artifact.title);
                const loadingId = processingArtifacts.current.get(key);

                if (loadingId) {
                    // Finish loading artifact
                    finishLoading(loadingId, {
                        type: artifact.type,
                        title: artifact.title,
                        content: artifact.content,
                        threadId,
                        language: artifact.language,
                    } as any);
                    completedArtifacts.current.add(key);
                } else if (!completedArtifacts.current.has(key)) {
                    // Check if this artifact already exists in the thread to prevent duplicates
                    const existingThreadArtifacts = getAllThreadItems(threadId);
                    const isDuplicate = existingThreadArtifacts.some(
                        (existing) =>
                            !('isLoading' in existing) && // Only check completed artifacts
                            existing.title === artifact.title &&
                            existing.type === artifact.type &&
                            JSON.stringify(existing.content) === JSON.stringify(artifact.content)
                    );

                    if (!isDuplicate) {
                        // Add artifact directly if we didn't track it during streaming
                        addArtifact({
                            type: artifact.type,
                            title: artifact.title,
                            content: artifact.content,
                            threadId,
                            language: artifact.language,
                        } as any);
                    }
                    completedArtifacts.current.add(key);
                }
            });

            // Clear processing references
            processingArtifacts.current.clear();
        }
    }, [
        content,
        isStreaming,
        role,
        threadId,
        addArtifact,
        startLoading,
        updateLoadingContent,
        finishLoading,
        getThreadLoadingArtifacts,
        getAllThreadItems,
    ]);

    // Reset refs when message changes
    useEffect(() => {
        processingArtifacts.current.clear();
        // Don't clear completedArtifacts - keep track across messages in the same thread
        // Only clear when thread changes
    }, [messageId]);

    // Reset refs when thread changes  
    useEffect(() => {
        processingArtifacts.current.clear();
        completedArtifacts.current.clear();
    }, [threadId]);

    // Process display content
    const { displayContent, artifacts, hasArtifacts } = useMemo(() => {
        if (role !== "assistant") {
            return { displayContent: content, artifacts: [], hasArtifacts: false };
        }

        let processedContent = content;
        let artifactsData: ParsedArtifactData[] = [];

        if (isStreaming) {
            // During streaming, extract artifacts but don't add button placeholders
            const messageArtifactKeys = Array.from(processingArtifacts.current.entries());
            const messageLoadingArtifacts: ParsedArtifactData[] = [];

            // Get all thread items to find matching artifacts
            const allThreadItems = getAllThreadItems(threadId);

            // Match loading artifacts to this message
            messageArtifactKeys.forEach(([, loadingId]) => {
                const artifact = allThreadItems.find((item) => item.id === loadingId);

                if (artifact && "isLoading" in artifact && artifact.isLoading) {
                    messageLoadingArtifacts.push({
                        type: artifact.type as ArtifactType,
                        title: artifact.title,
                        content: artifact.partialContent?.content || "",
                        language: artifact.language || artifact.partialContent?.language,
                    });
                }
            });

            // Remove artifact tags from content during streaming
            processedContent = content
                .replace(/<SlatePanel\s+type="[^"]+"[^>]*>[\s\S]*?(?:<\/SlatePanel>|$)/g, "")
                .trim();

            artifactsData = messageLoadingArtifacts;
        } else {
            // Not streaming - parse completed artifacts
            const result = parseArtifactElements(content);
            // Remove artifact tags from content
            processedContent = result.processedContent.replace(/\[\[ARTIFACT_PLACEHOLDER_\d+\]\]/g, "").trim();
            artifactsData = result.artifacts;
        }

        return {
            displayContent: processedContent,
            artifacts: artifactsData,
            hasArtifacts: artifactsData.length > 0,
        };
    }, [content, isStreaming, role, threadId, getAllThreadItems]);

    return { displayContent, artifacts, hasArtifacts };
};
